import React, { useState, useMemo } from "react"
import { Alert } from "@ui/Alert"
import { Button } from "@ui/Button"
import { Dialog } from "@ui/Dialog"
import { Input } from "@ui/Input"

export function AddMappingModal({
  isOpen,
  title,
  availableSpaces,
  currentlyMappedIds,
  isSubmitting,
  error,
  onAdd,
  onCancel,
  spaceNameField = "name",
  secondaryNameField = null
}) {
  const [ searchTerm, setSearchTerm ] = useState("")
  const [ selectedSpaceId, setSelectedSpaceId ] = useState(null)

  const filteredSpaces = useMemo(() => {
    const mappedSet = new Set(currentlyMappedIds || [])
    return (availableSpaces || [])
      .filter(space => !mappedSet.has(space.id))
      .filter(space => {
        if (!searchTerm.trim()) return true
        const searchLower = searchTerm.toLowerCase()
        const primaryName = space[spaceNameField] || ""
        const secondaryName = secondaryNameField ? (space[secondaryNameField] || "") : ""
        return primaryName.toLowerCase().includes(searchLower) ||
               secondaryName.toLowerCase().includes(searchLower)
      })
  }, [ availableSpaces, currentlyMappedIds, searchTerm, spaceNameField, secondaryNameField ])

  function handleClose() {
    setSearchTerm("")
    setSelectedSpaceId(null)
    onCancel()
  }

  function handleAdd() {
    if (selectedSpaceId) {
      onAdd(selectedSpaceId)
    }
  }

  React.useEffect(() => {
    if (!isOpen) {
      setSearchTerm("")
      setSelectedSpaceId(null)
    }
  }, [ isOpen ])

  return (
    <Dialog.Root open={isOpen} onOpenChange={(o) => { if (!o) handleClose() }}>
      <Dialog.Content size="sm">
        <Dialog.Header>
          <Dialog.Title>{title}</Dialog.Title>
        </Dialog.Header>

        <Dialog.Body>
          <div className="space-y-4">
            <Input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search..."
            />

            <div className="max-h-64 overflow-y-auto border border-webex-gray-300 dark:border-webex-gray-700 rounded-md">
              {filteredSpaces.length === 0 ? (
                <div className="p-4 text-sm text-webex-gray-600 dark:text-webex-gray-400 text-center">
                  {searchTerm ? "No matching spaces found" : "No available spaces"}
                </div>
              ) : (
                <ul className="divide-y divide-webex-gray-200 dark:divide-webex-gray-800">
                  {filteredSpaces.map((space) => {
                    const secondaryName = secondaryNameField ? space[secondaryNameField] : null
                    return (
                      <li key={space.id}>
                        <button
                          type="button"
                          onClick={() => setSelectedSpaceId(space.id)}
                          className={`w-full px-4 py-3 text-left transition-colors motion-reduce:transition-none ${
                            selectedSpaceId === space.id
                              ? "bg-webex-blue-100 dark:bg-webex-blue-900"
                              : "hover:bg-webex-gray-50 dark:hover:bg-webex-gray-900"
                          }`}
                        >
                          <div className={`text-sm ${
                            selectedSpaceId === space.id
                              ? "text-webex-blue-700 dark:text-webex-blue-400"
                              : "text-webex-gray-1000 dark:text-white"
                          }`}>
                            {space[spaceNameField]}
                          </div>
                          {secondaryName && (
                            <div className={`text-xs mt-0.5 ${
                              selectedSpaceId === space.id
                                ? "text-webex-blue-700 dark:text-webex-blue-400"
                                : "text-webex-gray-600 dark:text-webex-gray-400"
                            }`}>
                              {secondaryName}
                            </div>
                          )}
                        </button>
                      </li>
                    )
                  })}
                </ul>
              )}
            </div>

            {error && (
              <Alert variant="error">
                <Alert.Icon />
                <Alert.Content>
                  <Alert.Description>{error}</Alert.Description>
                </Alert.Content>
              </Alert>
            )}
          </div>
        </Dialog.Body>

        <Dialog.Footer>
          <Button variant="secondary" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleAdd} disabled={isSubmitting || !selectedSpaceId}>
            {isSubmitting ? "Adding..." : "Add"}
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog.Root>
  )
}

export function RemoveMappingConfirmModal({
  isOpen,
  spaceName,
  mappedToName,
  isSubmitting,
  error,
  onConfirm,
  onCancel
}) {
  return (
    <Dialog.Root open={isOpen} onOpenChange={(o) => { if (!o) onCancel() }}>
      <Dialog.Content size="md">
        <Dialog.Header>
          <Dialog.Title>Remove Mapping</Dialog.Title>
        </Dialog.Header>

        <Dialog.Body>
          <div className="text-sm text-webex-gray-700 dark:text-webex-gray-300 space-y-3">
            <p>
              Are you sure you want to remove the mapping between{" "}
              <strong className="text-webex-gray-1000 dark:text-white">"{spaceName}"</strong>
              {" "}and{" "}
              <strong className="text-webex-gray-1000 dark:text-white">"{mappedToName}"</strong>?
            </p>
            <p>
              Existing meetings and bookings will not be affected, but future events
              will not automatically create bookings for this combination.
            </p>
          </div>

          {error && (
            <Alert variant="error" className="mt-4">
              <Alert.Icon />
              <Alert.Content>
                <Alert.Description>{error}</Alert.Description>
              </Alert.Content>
            </Alert>
          )}
        </Dialog.Body>

        <Dialog.Footer>
          <Button variant="secondary" onClick={onCancel} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button variant="cancel" onClick={onConfirm} disabled={isSubmitting}>
            {isSubmitting ? "Removing..." : "Remove Mapping"}
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog.Root>
  )
}
